import { Spine as pixiSpine } from 'pixi-spine';
import {
  AnimationState,
  AnimationStateData,
  ClippingAttachment,
  MeshAttachment,
  RegionAttachment,
  Skeleton,
} from '@pixi-spine/runtime-4.1';
import { Assets } from 'pixi.js';
import { slotSpineKey } from '../../../constants/symbolsNames';

// RU: события, которые AnimationState отдаёт слушателю под собственными именами. Любое другое
//     имя — пользовательское событие таймлайна: оно приходит в `event` с `data.name`.
const TRACK_EVENTS = ['start', 'interrupt', 'end', 'dispose', 'complete'];

export class Spine extends pixiSpine {
  // RU: ключ — номер символа или имя ресурса из `slotSpineKey`. Полей класса здесь
  //     намеренно нет: инициализатор поля срабатывает ПОСЛЕ конструктора pixi-spine и
  //     затирал его значения (так терялся `stateData`).
  constructor({ key }) {
    const { spineData } = Assets.get(slotSpineKey[key]);
    super(spineData);
    this.key = key;
  }

  // RU: `lastTime = 0` — первый кадр после показа идёт с нулевой дельтой: pixi-spine считает
  //     её от Date.now(), и без сброса анимация прыгнула бы на время, пока спайн стоял.
  show() {
    this.visible = true;
    this.lastTime = 0;
  }

  play(animationName, loop = false, timeScale = 1) {
    this.lastTime = 0;
    if (this.state?.hasAnimation(animationName)) {
      this.state.setAnimation(0, animationName, loop).timeScale = timeScale;
    }
  }

  changeTexture(key) {
    if (this.key === key) return;
    this.key = key;
    this.removeChildren();
    const { spineData } = Assets.get(slotSpineKey[key]);
    this.spineData = spineData;
    this.skeleton = new Skeleton(spineData);
    this.skeleton.updateWorldTransform();
    this.stateData = new AnimationStateData(spineData);
    // RU: новый AnimationState уносит с собой всех слушателей прежнего — и повешенных на его
    //     треки, и общих: отдельного реестра, который пришлось бы чистить, у спайна нет.
    this.state = new AnimationState(this.stateData);
    this.slotContainers = [];
    this.tempClipContainers = [];
    for (let i = 0, n = this.skeleton.slots.length; i < n; i += 1) {
      const slot = this.skeleton.slots[i];
      const { attachment } = slot;
      const slotContainer = this.newContainer();
      this.slotContainers.push(slotContainer);
      this.addChild(slotContainer);
      this.tempClipContainers.push(null);
      if (attachment instanceof RegionAttachment) {
        const spriteName = attachment?.region?.name;
        const sprite = this.createSprite(slot, attachment, spriteName);
        slot.currentSprite = sprite;
        slot.currentSpriteName = spriteName;
        slotContainer.addChild(sprite);
      } else if (attachment instanceof MeshAttachment) {
        const mesh = this.createMesh(slot, attachment);
        slot.currentMesh = mesh;
        slot.currentMeshName = attachment.name;
        slotContainer.addChild(mesh);
      } else if (attachment instanceof ClippingAttachment) {
        this.createGraphics(slot, attachment);
        slotContainer.addChild(slot.clippingContainer);
        slotContainer.addChild(slot.currentGraphics);
      }
    }
  }

  accelerateCurrentAnimation(factor) {
    const animation = this.state.tracks[0];
    if (animation) {
      animation.timeScale *= factor;
    }
  }

  stop() {
    this.state.clearTracks();
    this.skeleton.setToSetupPose();
  }

  getTrackByAnimationName(animationName) {
    return animationName
      ? this.state.tracks.find((track) => track && track.animation.name === animationName)
      : null;
  }

  /**
   * RU: одноразовый слушатель события анимации — других в игре нет: каждый ждёт ровно одно
   *     событие. Играет трек с `animationName` — слушатель вешается на ЭТОТ его запуск
   *     (TrackEntry) и умирает вместе с ним. Трека нет (анимации нет в скелете или она ещё не
   *     запущена) — слушатель общий для AnimationState и ждёт первого такого события любого
   *     трека, как и прежде.
   *
   *     Снятие ОТЛОЖЕНО до конца раздачи событий (engine-gotcha №16): `EventQueue.drain`
   *     обходит живой массив слушателей без копии, и синхронный `splice` из колбэка сдвигал
   *     соседа на освободившийся индекс — тот на этом событии не срабатывал. Повторное
   *     срабатывание до снятия (зацикленный трек шлёт `complete` на каждом круге) отсекает
   *     флаг `done`.
   */
  onceAnimationEvent(event, callback, animationName = null) {
    const isTrackEvent = TRACK_EVENTS.includes(event);
    const eventKey = isTrackEvent ? event : 'event';
    const { state } = this;
    const track = this.getTrackByAnimationName(animationName);
    let done = false;
    let detach = null;
    const handler = (entry, spineEvent) => {
      if (done || (!isTrackEvent && spineEvent?.data?.name !== event)) return;
      done = true;
      Promise.resolve().then(detach);
      callback(entry, spineEvent);
    };
    if (track) {
      track.listener = { ...track.listener, [eventKey]: handler };
      // RU: проверка владельца обязательна: TrackEntry возвращается в пул и к моменту снятия
      //     может уже принадлежать другой анимации со своим слушателем.
      detach = () => {
        if (track.listener?.[eventKey] === handler) delete track.listener[eventKey];
      };
    } else {
      const listener = { [eventKey]: handler };
      state.addListener(listener);
      detach = () => state.removeListener(listener);
    }
  }

  addChildToSlot(slotName, child, index = 0) {
    const slot = this.getSlot(slotName);
    if (!slot) return false;
    (slot.children[index] || slot).addChild(child);
    return true;
  }

  // RU: контейнер слота — из `slotContainers`, а НЕ `children[index]`. pixi-spine пишет `children` по
  //     ПОРЯДКУ ОТРИСОВКИ: таймлайн `drawOrder` ставит на место i контейнер другого слота, а под
  //     clipping-аттачментом на место контейнера встаёт скрытая заглушка (`tempClipContainers`) —
  //     вставленное туда после первого кадра невидимо и не анимируется (G1 honey f0fa396e).
  // EN: slot containers come from `slotContainers`: `children` follows the draw order and holds hidden
  //     placeholders under a clipping attachment.
  getSlot(slotName) {
    const index = this.spineData.slots.findIndex((slotData) => slotData.name === slotName);
    return index >= 0 ? this.slotContainers[index] : undefined;
  }

  goToAnimationEnd(name) {
    const track = this.getTrackByAnimationName(name);
    if (track) {
      track.trackTime = track.animationEnd;
    }
  }
}
